import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useBlogs } from '../hooks/useData';

const BlogPage = () => {
  const { blogs, loading } = useBlogs();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="pt-24 pb-16 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Blog & Insights
            </h1>
            <p className="text-lg text-gray-600">
              Expert guides, tool comparisons, and industry insights
            </p>
          </div>

          {/* Featured Post */}
          {blogs[0] && (
            <Link
              to={`/blog/${blogs[0].slug}`}
              className="block bg-white rounded-2xl border border-gray-100 overflow-hidden mb-12 hover:shadow-xl hover:shadow-gray-100/50 transition-all group"
            >
              <div className="grid md:grid-cols-2">
                <div className="h-64 md:h-auto overflow-hidden">
                  <img
                    src={blogs[0].image}
                    alt={blogs[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full mb-4 w-fit">
                    Featured
                  </span>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors">
                    {blogs[0].title}
                  </h2>
                  <p className="text-gray-600 mb-6">{blogs[0].excerpt}</p>
                  <div className="flex items-center gap-4">
                    <img
                      src={blogs[0].authorAvatar}
                      alt={blogs[0].author}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-medium text-gray-900">
                        {blogs[0].author}
                      </div>
                      <div className="text-sm text-gray-500">
                        {blogs[0].date} · {blogs[0].readTime}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.slice(1).map((blog) => (
              <article
                key={blog.id}
                className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:shadow-xl hover:shadow-gray-100/50 transition-all duration-300"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs text-emerald-600 font-medium">
                    {blog.category}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 mt-2 mb-3 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                    <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                  </h3>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                    {blog.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                    <div className="flex items-center gap-2">
                      <img
                        src={blog.authorAvatar}
                        alt={blog.author}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-xs text-gray-600">
                        {blog.author}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      {blog.readTime}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;